import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { IReportSettingOption, ReportColumnSizingType, ReportDisplayType, ReportGridService } from '@app/shared/report/report-grid.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import _ from 'underscore';
import { util } from '@app/shared/util';
import { IExtendedDocument } from '@type_definitions/datatypes';
import { Subscription } from 'rxjs';
import { IReportAggregates } from '@app/shared/report/report-data.service';
import { DOCUMENT } from '@angular/common';

export const aggregateModels:string[] = ['sum', 'count', 'average', 'min', 'max'];

export interface IReportTableSettingsModel extends IReportAggregates {
  columnSizing?:IReportSettingOption<ReportColumnSizingType>;
  display?:ReportDisplayType;
}

export type OnResetLayoutSettingsDoneCallback = () => void;

@Component({
  selector: 'report-table-settings',
  template: `
    <div class="layout-control float-end hidden-xxs" dropdown [autoClose]="true" [insideClick]="true" placement="bottom right">
      <button class="btn dropdown-arrow-btn dropdown-toggle" id="layout-control-menu" dropdownToggle translate>
        REPORTS.BUTTONS.TABLE_SETTINGS
        <span class="caret"></span>
      </button>
      <ul class="dropdown-menu dropdown-menu-right layout-control-menu" *dropdownMenu aria-labelledby="layout-control-menu" role="menu">
        <li>
          <formly [fields]="fields" [model]="model" [form]="form"></formly>
        </li>
      </ul>
    </div>
  `
})
export class ReportTableSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public reportId:string;
  @Input() public model:IReportTableSettingsModel = {};
  
  @Output() public modelChange:EventEmitter<IReportTableSettingsModel> = new EventEmitter<IReportTableSettingsModel>();
  @Output() public onResetLayoutSettings:EventEmitter<OnResetLayoutSettingsDoneCallback> = new EventEmitter<OnResetLayoutSettingsDoneCallback>();

  public fields:IExtraFieldConfigurationObject[] = [];
  public form:UntypedFormGroup = new UntypedFormGroup({});

  private formValueChangesSubscription:Subscription;
  private formPatchValueTimeout:number;
  private resetLayoutSettingsButtonField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private reportGridService:ReportGridService,
    @Inject(DOCUMENT) private document:IExtendedDocument
  ) {
    /**/
  }

  public ngOnInit():void {
    this.fields = this.createFields();
    // register listener
    this.listen();
  }

  public ngOnChanges(changes:SimpleChanges) {
    if (changes.model) {
      clearTimeout(this.formPatchValueTimeout);

      // add timeout so form is correctly set
      this.formPatchValueTimeout = setTimeout(() => {
        // do not emit event, if we receive model from consumer component we don't need to trigger 'valueChanges' subscription
        this.form.patchValue(changes.model.currentValue, { emitEvent: false });

        if (!changes.model.isFirstChange() && !_.isEqual(changes.model.currentValue, changes.model.previousValue)) {
          this.triggerFieldsChange(changes.model.currentValue, changes.model.previousValue);
        }
      });
    }
  }

  private listen():void {
    this.formValueChangesSubscription = this.form.valueChanges.subscribe((model:IReportTableSettingsModel) => {
      this.modelChange.emit(model);
    });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-md-12';

    return [
      this.formlyService.aRadio('columnSizing', 'Column sizing')
        .withOptions(this.reportGridService.sizingOptions)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(fullWidthClass)
        .withValueClass(`${fullWidthClass} col-space-minus-top`)
        .withWrapperClass(fullWidthClass)
        .withoutEditable()
        .onChange(this.onColumnSizingChange)
        .build(),

      this.formlyService.aGroup('aggregateGroup', 'Calculate')
        .withLabelClass(fullWidthClass)
        .withValueClass(`${fullWidthClass} col-space-minus-top`)
        .withFields(
          _.map(aggregateModels, (value:string):IExtraFieldConfigurationObject => {
            const label:string = util.capitalize(value);
            return this.formlyService.aCheckbox(value, label)
              .withoutWrapper()
              .withWrapperClass(fullWidthClass)
              .onChange(this.onAggregateChange)
              .build();
          })
        )
        .build(),

      this.formlyService.aRadio('display', 'Display')
        .withOptions(this.reportGridService.reportDisplays)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(fullWidthClass)
        .withValueClass(`${fullWidthClass} col-space-minus-top`)
        .withWrapperClass(fullWidthClass)
        .withoutEditable()
        .withLabelProp('description')
        .withValueProp('id')
        .onChange(this.onDisplayTypeChange)
        .build(),
      
      this.resetLayoutSettingsButtonField = this.formlyService.aLink('Reset layout settings')
        .withoutWrapper()
        .withValueClass('margin-bottom-sm')
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn btn-default'
          }
        })
        .onClick(this.onClickResetLayoutSettings)
        .build()
    ];
  }

  private triggerFieldsChange(newValue:IReportTableSettingsModel, oldValue:IReportTableSettingsModel):void {
    if (!_.isEqual(newValue?.display, oldValue?.display)) {
      this.onDisplayTypeChange(this.model.display);
    }

    if (!_.isEqual(newValue?.columnSizing?.id, oldValue?.columnSizing?.id)) {
      this.onColumnSizingChange(this.model.columnSizing);
    }
  }

  // update the model when the aggregate value is changed
  private onAggregateChange = (checkboxValue:string, checkboxField:IExtraFieldConfigurationObject):void => {
    this.form.patchValue({ [<string>checkboxField.key]: checkboxValue });
    this.reportGridService.setAggregates(this.reportId);
  };

  private onColumnSizingChange = (value:IReportSettingOption<ReportColumnSizingType>):void => {
    this.reportGridService.setColumnSizing(this.reportId, value, true);
  };

  private onDisplayTypeChange = (value:ReportDisplayType):void => {
    if (!value) {
      return;
    }

    const sizes:string[] = ['normal', 'compact'];
    const el:HTMLElement = this.document.querySelector<HTMLElement>('[class*="ag-theme-quartz"]');

    if (el) {
      const currentSize:string = value === ReportDisplayType.NORMAL ? sizes[0] : sizes[1];
      sizes.forEach((size:string) => el.classList.toggle(size, size === currentSize));
    }

    this.reportGridService.setDisplayType(this.reportId, value);
  };
  
  private onClickResetLayoutSettings = ():void => {
    this.resetLayoutSettingsButtonField.templateOptions.busy = true;
    this.onResetLayoutSettings.emit(() => {
      this.resetLayoutSettingsButtonField.templateOptions.busy = false;
    });
  };

  public ngOnDestroy():void {
    this.formValueChangesSubscription?.unsubscribe();
  }
}